/* Fluffball Club theme — vanilla JS, no dependencies */
(() => {
  const FB = window.FB || { routes: {}, moneyFormat: '${{amount}}', cartRecs: true };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduceMotion && 'IntersectionObserver' in window) document.documentElement.classList.add('js-reveal');

  /* ---------- Money ---------- */
  function formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const [int, frac] = n.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return String(FB.moneyFormat).replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        default: return fmt(value, 2);
      }
    }).replace(/<[^>]*>/g, '');
  }
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- Quick add buttons for JS-rendered cards ---------- */
  const PLUS = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
  const productCache = new Map();
  // Single-variant products are added straight away. Products with options (size, color)
  // open a small picker in place, so nobody gets the wrong size by accident.
  function quickAddButton(p, cls, withLabel) {
    const available = (p.variants || []).filter((v) => v.available);
    if (!available.length) return '';
    productCache.set(String(p.id), p);
    const label = withLabel ? '<span>Add</span>' : '';
    if (p.variants.length === 1) {
      return `<button type="button" class="${cls}" data-quick-add="${available[0].id}" aria-label="Add ${escapeHtml(p.title)} to cart">${PLUS}${label}</button>`;
    }
    return `<button type="button" class="${cls}" data-quick-pick="${p.id}" aria-expanded="false" aria-label="Choose options and add ${escapeHtml(p.title)}">${PLUS}${label}</button>`;
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }

  /* ---------- Cart drawer ---------- */
  const drawer = $('[data-cart-drawer]');
  let lastFocus = null;

  function openDrawer() {
    if (!drawer) { window.location.href = FB.routes.cart; return; }
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    $('.drawer__panel', drawer).focus();
    loadCartRecs(drawer);
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus) lastFocus.focus();
  }

  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    $$('[data-cart-open]').forEach((el) => el.setAttribute('aria-label', `Cart, ${count} items`));
  }

  async function refreshCart() {
    const mainCart = $('[data-main-cart]');
    const ids = ['cart-drawer'];
    if (mainCart) ids.push(mainCart.dataset.sectionId);
    const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    const data = await res.json();
    const parser = new DOMParser();

    if (drawer && data['cart-drawer']) {
      const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
      const fresh = $('[data-drawer-inner]', doc);
      if (fresh) $('[data-drawer-inner]', drawer).innerHTML = fresh.innerHTML;
      if (drawer.classList.contains('is-open')) loadCartRecs(drawer);
    }
    if (mainCart && data[mainCart.dataset.sectionId]) {
      const doc = parser.parseFromString(data[mainCart.dataset.sectionId], 'text/html');
      const fresh = $('[data-main-cart]', doc);
      if (fresh) { mainCart.innerHTML = fresh.innerHTML; loadCartRecs(mainCart); }
    }
    const cart = await fetch(`${FB.routes.cart}.js`).then((r) => r.json());
    updateCount(cart.item_count);
    staggerLines();
    return cart;
  }

  function bumpCart() {
    $$('.cart-btn').forEach((b) => { b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump'); });
  }
  function staggerLines() {
    $$('.litems .litem').forEach((el, i) => el.style.setProperty('--i', i));
  }

  async function addToCart(variantId, quantity = 1) {
    const res = await fetch(`${FB.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }] })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add this item.');
    await refreshCart();
    bumpCart();
    return data;
  }

  async function changeLine(line, quantity) {
    const lists = $$('.litems');
    lists.forEach((l) => l.classList.add('is-busy'));
    try {
      const res = await fetch(`${FB.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)) })
      });
      if (!res.ok) {
        const data = await res.json();
        toast(data.description || 'Could not update the cart.');
      }
      await refreshCart();
    } finally {
      lists.forEach((l) => l.classList.remove('is-busy'));
    }
  }

  /* ---------- Cart recommendations ("Goes well with") ---------- */
  async function fetchRecs(productId, intent) {
    const url = `${FB.routes.recs}.json?product_id=${productId}&limit=10&intent=${intent}`;
    try {
      const r = await fetch(url);
      if (!r.ok) return [];
      const d = await r.json();
      return d.products || [];
    } catch { return []; }
  }

  async function loadCartRecs(root) {
    if (!FB.cartRecs) return;
    const box = $('[data-cart-recs]', root);
    if (!box || box.dataset.loaded) return;
    box.dataset.loaded = '1';
    const first = box.dataset.first;
    if (!first) return;
    const inCart = new Set((box.dataset.inCart || '').split(',').map(Number));
    let products = await fetchRecs(first, 'complementary');
    if (products.length < 2) products = products.concat(await fetchRecs(first, 'related'));
    const seen = new Set();
    products = products.filter((p) => {
      if (inCart.has(p.id) || seen.has(p.id) || !p.available) return false;
      seen.add(p.id);
      return true;
    }).slice(0, 6);
    if (!products.length) return;

    $('[data-cart-recs-list]', box).innerHTML = products.map((p) => {
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=300` : '';
      return `<li class="crec">
        <a href="${p.url}" tabindex="-1" aria-hidden="true">${img ? `<img class="crec__img" src="${img}" alt="" loading="lazy" width="148" height="148">` : ''}</a>
        <div class="crec__body">
          <a class="crec__title" href="${p.url}">${escapeHtml(p.title)}</a>
          <div class="crec__row">
            <span class="crec__price">${formatMoney(p.price)}</span>
            ${quickAddButton(p, 'crec__add', false)}
          </div>
        </div>
      </li>`;
    }).join('');
    $$('.crec', box).forEach((el, i) => el.style.setProperty('--i', i));
    box.hidden = false;
  }

  function toggleQuickPick(btn) {
    const host = btn.closest('.crec__body, .card__body');
    const open = host.querySelector('.qpick');
    if (open) { open.remove(); btn.setAttribute('aria-expanded', 'false'); return; }
    const p = productCache.get(btn.dataset.quickPick);
    if (!p) return;
    const options = p.variants.filter((v) => v.available)
      .map((v) => `<option value="${v.id}">${escapeHtml(v.title)} — ${formatMoney(v.price)}</option>`).join('');
    const id = `qpick-${p.id}-${Math.random().toString(36).slice(2, 7)}`;
    host.insertAdjacentHTML('beforeend', `<div class="qpick">
      <label class="visually-hidden" for="${id}">Choose option</label>
      <select class="qpick__select" id="${id}">${options}</select>
      <button type="button" class="qpick__add" data-qpick-add>Add</button>
    </div>`);
    btn.setAttribute('aria-expanded', 'true');
    host.querySelector('.qpick__select').focus();
  }

  /* ---------- Global click handling ---------- */
  document.addEventListener('click', async (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && drawer) { e.preventDefault(); openDrawer(); return; }

    if (e.target.closest('[data-drawer-close]')) { closeDrawer(); return; }

    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      quick.classList.add('is-loading');
      try {
        await addToCart(quick.dataset.quickAdd, 1);
        if (!drawer || !drawer.classList.contains('is-open')) openDrawer();
        else toast('Added to cart');
      } catch (err) { toast(err.message); }
      finally { quick.classList.remove('is-loading'); }
      return;
    }

    const pickBtn = e.target.closest('[data-quick-pick]');
    if (pickBtn) { e.preventDefault(); toggleQuickPick(pickBtn); return; }

    const pickAdd = e.target.closest('[data-qpick-add]');
    if (pickAdd) {
      e.preventDefault();
      const picker = pickAdd.closest('.qpick');
      const select = picker.querySelector('select');
      pickAdd.disabled = true;
      try {
        await addToCart(select.value, 1);
        picker.remove();
        if (!drawer || !drawer.classList.contains('is-open')) openDrawer();
        else toast('Added to cart');
      } catch (err) { toast(err.message); pickAdd.disabled = false; }
      return;
    }

    const lineBtn = e.target.closest('[data-line-change]');
    if (lineBtn) { changeLine(lineBtn.dataset.lineChange, lineBtn.dataset.qty); return; }

    const loadMore = e.target.closest('[data-load-more]');
    if (loadMore) { e.preventDefault(); handleLoadMore(loadMore); return; }

    const copy = e.target.closest('[data-copy]');
    if (copy) {
      const code = $('[data-copy-source]', copy.closest('.welcome__code'))?.textContent.trim();
      if (code && navigator.clipboard) {
        navigator.clipboard.writeText(code).then(() => { copy.textContent = 'Copied'; });
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (drawer && drawer.classList.contains('is-open')) closeDrawer();
    closeWelcome();
    const mnav = $('[data-mnav][open]');
    if (mnav) mnav.removeAttribute('open');
  });

  /* ---------- Load more (infinite grid) ---------- */
  async function handleLoadMore(btn) {
    const root = btn.closest('[data-load-more-root]');
    const grid = $('[data-load-more-grid]', root);
    const url = new URL(btn.href, window.location.origin);
    url.searchParams.set('section_id', btn.dataset.section);
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Loading…';
    try {
      const html = await fetch(url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = $$('[data-load-more-grid] > li', doc);
      const firstNew = items[0];
      items.forEach((li) => grid.appendChild(li));
      const next = $('[data-load-more]', doc);
      if (next) { btn.href = next.getAttribute('href'); btn.textContent = 'Show more'; btn.removeAttribute('aria-busy'); }
      else btn.closest('.pgrid__more').remove();
      const focusTarget = firstNew && $('a[href]:not([tabindex="-1"])', firstNew);
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    } catch {
      btn.textContent = 'Show more';
      btn.removeAttribute('aria-busy');
      toast('Could not load more products.');
    }
  }

  // Auto-load when the button scrolls into view (infinite scroll), button stays as fallback.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !entry.target.hasAttribute('aria-busy')) handleLoadMore(entry.target);
      });
    }, { rootMargin: '400px' });
    const watch = () => $$('[data-load-more]').forEach((b) => { if (!b.dataset.watched) { b.dataset.watched = '1'; io.observe(b); } });
    watch();
    new MutationObserver(watch).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- Sort ---------- */
  $$('[data-sort-select]').forEach((sel) => sel.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }));

  /* ---------- Product page ---------- */
  $$('[data-product-section]').forEach((section) => {
    const product = JSON.parse($('[data-product-json]', section).textContent);
    const form = $('[data-product-form]', section);
    const idInput = $('[data-variant-id]', section);
    const addBtn = $('[data-add-btn]', section);
    const errorEl = $('[data-form-error]', section);
    const stickyBar = $('[data-sticky-buy]', section);
    const stickyPrice = $('[data-sticky-price]', section);
    const stickyAdd = $('[data-sticky-add]', section);
    const gallery = $('[data-gallery]', section);
    const picker = $('[data-variant-picker]', section);

    // Thumbnails
    const thumbs = $$('[data-thumb]', section);
    const showMedia = (mediaId, smooth = true) => {
      const item = $(`[data-media-id="${mediaId}"]`, section);
      if (!item || !gallery) return;
      gallery.scrollTo({ left: item.offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.thumb === String(mediaId))));
    };
    thumbs.forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.thumb)));
    if (thumbs[0]) thumbs[0].setAttribute('aria-current', 'true');

    // Quantity steppers
    $$('[data-qty-step]', section).forEach((b) => b.addEventListener('click', () => {
      const input = $('.qty__input', b.closest('[data-qty]'));
      input.value = Math.max(1, (Number(input.value) || 1) + Number(b.dataset.qtyStep));
    }));

    // Variant picker
    const selectedOptions = () => $$('[data-option-index]:checked', section)
      .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
      .map((i) => i.value);

    const findVariant = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const markAvailability = (opts) => {
      $$('[data-option-index]', section).forEach((input) => {
        const idx = Number(input.dataset.optionIndex);
        const test = opts.slice();
        test[idx] = input.value;
        const ok = product.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
        input.nextElementSibling.classList.toggle('is-unavailable', !ok);
      });
    };

    const renderPrice = (variant) => {
      const saleWrap = $('[data-price-wrap]', section);
      const salePct = saleWrap && saleWrap.dataset.salePct ? Number(saleWrap.dataset.salePct) : 0;
      if (salePct && variant.price > 3000) {
        variant = Object.assign({}, variant, { compare_at_price: variant.price, price: variant.price - Math.round(variant.price * salePct / 100) });
      }
      const wrap = $('[data-price-wrap] [data-price]', section);
      if (!wrap) return;
      const was = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s class="price__was"><span class="visually-hidden">Regular price</span>${formatMoney(variant.compare_at_price)}</s>` : '';
      wrap.innerHTML = `<span class="price__now">${formatMoney(variant.price)}</span>${was}`;
    };

    const applyVariant = (variant) => {
      const label = variant ? (variant.available ? 'Add to cart' : 'Sold out') : 'Unavailable';
      const disabled = !variant || !variant.available;
      if (addBtn) { addBtn.textContent = label; addBtn.disabled = disabled; }
      if (stickyAdd) { stickyAdd.textContent = label; stickyAdd.disabled = disabled; }
      if (!variant) return;
      idInput.value = variant.id;
      renderPrice(variant);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (variant.featured_media) showMedia(variant.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    if (picker) {
      markAvailability(selectedOptions());
      picker.addEventListener('change', (e) => {
        const input = e.target.closest('[data-option-index]');
        if (!input) return;
        const lbl = $(`[data-option-label="${input.dataset.optionIndex}"]`, section);
        if (lbl) lbl.textContent = input.value;
        const opts = selectedOptions();
        markAvailability(opts);
        applyVariant(findVariant(opts));
      });
    }

    // Add to cart
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.hidden = true;
        const qty = $('.qty__input', form)?.value || 1;
        addBtn.disabled = true;
        const prev = addBtn.textContent;
        addBtn.textContent = 'Adding…';
        try {
          await addToCart(idInput.value, qty);
          openDrawer();
        } catch (err) {
          errorEl.textContent = err.message;
          errorEl.hidden = false;
        } finally {
          addBtn.textContent = prev;
          addBtn.disabled = false;
        }
      });
    }

    // Sticky add-to-cart (mobile): visible when the main button is off screen
    if (stickyBar && addBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        stickyBar.classList.toggle('is-visible', show);
        stickyBar.setAttribute('aria-hidden', String(!show));
        if (stickyAdd) stickyAdd.tabIndex = show ? 0 : -1;
      }).observe(addBtn);
    }

    // Recently viewed: remember this product
    try {
      const key = 'fb_recent';
      const list = JSON.parse(localStorage.getItem(key) || '[]').filter((h) => h !== product.handle);
      list.unshift(product.handle);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch { /* storage unavailable */ }
  });

  /* ---------- Recently viewed + recommendations ---------- */
  const imgUrl = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const miniCard = (p) => {
    const img = imgUrl(p.featured_image, 360);
    const media = img
      ? `<img class="card__img" src="${img}" alt="" loading="lazy">`
      : `<span class="card__placeholder"></span>`;
    return `<li><article class="card card--mini">
      <a class="card__media" href="${p.url}" tabindex="-1" aria-hidden="true">${media}</a>
      <div class="card__body">
        <h3 class="card__title"><a href="${p.url}">${escapeHtml(p.title)}</a></h3>
        <div class="price"><span class="price__now">${formatMoney(p.price)}</span></div>
        ${quickAddButton(p, 'card__add', true)}
      </div>
    </article></li>`;
  };

  $$('[data-recent]').forEach(async (box) => {
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem('fb_recent') || '[]'); } catch { /* storage unavailable */ }
    handles = handles.filter((h) => h !== box.dataset.current).slice(0, 8);

    const viewed = (await Promise.all(handles.map((h) =>
      fetch(`${FB.routes.root.replace(/\/$/, '')}/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    ))).filter(Boolean);

    const viewedBlock = $('[data-recent-viewed]', box);
    if (viewed.length) {
      $('[data-recent-list]', box).innerHTML = viewed.map(miniCard).join('');
      viewedBlock.hidden = false;
      box.hidden = false;
    }

    const recsBlock = $('[data-recent-recs]', box);
    if (!recsBlock) return;
    const seed = box.dataset.currentId || (viewed[0] && viewed[0].id);
    if (!seed) return;
    const skip = new Set(viewed.map((p) => p.id).concat(Number(box.dataset.currentId) || []));
    let recs = await fetchRecs(seed, 'related');
    if (viewed[1] && recs.length < 6) recs = recs.concat(await fetchRecs(viewed[1].id, 'related'));
    const seen = new Set();
    recs = recs.filter((p) => {
      if (skip.has(p.id) || seen.has(p.id) || !p.available) return false;
      seen.add(p.id);
      return true;
    }).slice(0, Number(box.dataset.recsLimit) || 8);
    if (!recs.length) return;
    $('[data-recent-recs-list]', box).innerHTML = recs.map(miniCard).join('');
    recsBlock.hidden = false;
    box.hidden = false;
  });

  /* ---------- Clean supplier descriptions (duplicate photos) ---------- */
  $$('[data-desc-clean]').forEach((desc) => {
    $$('img', desc).forEach((img) => img.remove());
    // Drop leftover "Product Image:" style headings and now-empty wrappers
    $$('p, div, span, strong, b, h2, h3, h4', desc).forEach((el) => {
      const text = el.textContent.replace(/\s+/g, ' ').trim();
      if (/^(product\s*)?(image|images|picture|pictures|photo|photos)\s*:?$/i.test(text)) el.remove();
    });
    $$('p, div, span', desc).forEach((el) => {
      if (!el.textContent.trim() && !el.querySelector('video, iframe, table')) el.remove();
    });
  });

  /* ---------- Christmas order timer (product page) ---------- */
  const splitTime = (ms) => ({
    d: Math.floor(ms / 86400000),
    h: Math.floor((ms % 86400000) / 3600000),
    m: Math.floor((ms % 3600000) / 60000)
  });
  $$('[data-htimer]').forEach((box) => {
    const end = new Date(box.dataset.deadline).getTime();
    if (Number.isNaN(end)) return;
    box.hidden = false;
    const tick = () => {
      const left = end - Date.now();
      if (left <= 0) {
        $$('[data-htimer-live]', box).forEach((el) => { el.hidden = true; });
        $('[data-htimer-late]', box).hidden = false;
        box.classList.add('htimer--late');
        return false;
      }
      const t = splitTime(left);
      $('[data-d]', box).textContent = t.d;
      $('[data-h]', box).textContent = t.h;
      $('[data-m]', box).textContent = String(t.m).padStart(2, '0');
      return true;
    };
    if (tick()) {
      const timer = setInterval(() => { if (!tick()) clearInterval(timer); }, 15000);
    }
  });

  /* ---------- Announcement countdown (real deadline only) ---------- */
  $$('[data-deadline]').forEach((bar) => {
    const raw = bar.dataset.deadline;
    const clock = $('[data-countdown]', bar);
    if (!raw || !clock) return;
    const end = new Date(raw).getTime();
    if (Number.isNaN(end)) return;
    const tick = () => {
      const left = end - Date.now();
      if (left <= 0) {
        clock.hidden = true;
        const after = bar.dataset.after;
        if (after) $('[data-announce-text]', bar).textContent = after;
        return false;
      }
      const d = Math.floor(left / 86400000);
      const h = Math.floor((left % 86400000) / 3600000);
      const m = Math.floor((left % 3600000) / 60000);
      clock.textContent = `${d}d ${h}h ${String(m).padStart(2, '0')}m left to order`;
      clock.hidden = false;
      return true;
    };
    if (tick()) setInterval(tick, 30000);
  });

  /* ---------- Welcome offer ---------- */
  const welcome = $('[data-welcome]');
  function openWelcome() {
    if (!welcome) return;
    welcome.classList.add('is-open');
    welcome.setAttribute('aria-hidden', 'false');
    $('.welcome__panel', welcome).focus();
  }
  function closeWelcome() {
    if (!welcome || !welcome.classList.contains('is-open')) return;
    welcome.classList.remove('is-open');
    welcome.setAttribute('aria-hidden', 'true');
    try { localStorage.setItem('fb_welcome', String(Date.now())); } catch { /* ignore */ }
  }
  if (welcome) {
    $$('[data-welcome-close]', welcome).forEach((b) => b.addEventListener('click', closeWelcome));
    if (welcome.hasAttribute('data-welcome-success')) {
      openWelcome();
    } else {
      let seen = 0;
      try { seen = Number(localStorage.getItem('fb_welcome') || 0); } catch { /* ignore */ }
      const twoWeeks = 14 * 86400000;
      if (Date.now() - seen > twoWeeks && !document.body.classList.contains('template-cart')) {
        setTimeout(() => {
          if (!drawer || !drawer.classList.contains('is-open')) openWelcome();
        }, (Number(welcome.dataset.delay) || 10) * 1000);
      }
    }
  }

  /* ---------- Ticker: smooth glide that eases to a stop on hover / touch ---------- */
  $$('.ticker').forEach((ticker) => {
    const track = $('[data-ticker]', ticker);
    const first = track && $('.ticker__list', track);
    if (!track || !first || reduceMotion) return;
    ticker.classList.add('ticker--js');
    const duration = parseFloat(getComputedStyle(ticker).getPropertyValue('--ticker-duration')) || 60;
    let loop = first.getBoundingClientRect().width;
    let cruise = loop / duration;            // px per second at full speed
    let speed = cruise;
    let target = cruise;
    let offset = 0;
    let last = performance.now();
    let visible = true;
    let resumeTimer;

    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      speed += (target - speed) * (1 - Math.exp(-dt * 5));   // smooth ease toward target speed
      offset = (offset + speed * dt) % loop;
      track.style.transform = `translate3d(${-offset}px,0,0)`;
      if (visible) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);

    const slow = () => { clearTimeout(resumeTimer); target = 0; };
    const go = (delay = 0) => { clearTimeout(resumeTimer); resumeTimer = setTimeout(() => { target = cruise; }, delay); };
    ticker.addEventListener('mouseenter', slow);
    ticker.addEventListener('mouseleave', () => go());
    ticker.addEventListener('focusin', slow);
    ticker.addEventListener('focusout', () => go());
    ticker.addEventListener('touchstart', slow, { passive: true });
    ticker.addEventListener('touchend', () => go(1800), { passive: true });

    new IntersectionObserver(([e]) => {
      const was = visible;
      visible = e.isIntersecting;
      if (visible && !was) { last = performance.now(); requestAnimationFrame(frame); }
    }).observe(ticker);
    window.addEventListener('resize', () => {
      loop = first.getBoundingClientRect().width;
      cruise = loop / duration;
      if (target) target = cruise;
    });
  });

  /* ---------- Scroll reveal ---------- */
  if (document.documentElement.classList.contains('js-reveal')) {
    const revealIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); revealIO.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    const watchReveal = () => {
      $$('[data-reveal]:not([data-revealed])').forEach((el) => {
        el.dataset.revealed = '1';
        const siblings = el.parentElement ? Array.from(el.parentElement.children) : [];
        if (!el.style.getPropertyValue('--i')) el.style.setProperty('--i', Math.max(0, siblings.indexOf(el)) % 6);
        revealIO.observe(el);
      });
    };
    watchReveal();
    new MutationObserver(watchReveal).observe(document.body, { childList: true, subtree: true });
  }
})();
